import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import {
  administrationMenuEntityList,
  campaignMenuEntityList,
  Entity,
  notificationsHubMenuEntityList,
} from 'ag-common-lib/public-api';
import { AdminCrmRoutes } from 'src/app/admin-crm/admin-crm.model';
import { AppRoutes } from 'src/app/app.model';
import { map } from 'rxjs';
import { CampaignsRoutes } from 'src/app/ag-campaigns/ag-campaigns.model';
import { ARMAuthService } from 'src/app/shared/services/util/arm-auth.service';
import { NotificationsHubRoutes } from 'src/app/ag-notifications-hub/ag-notifications-hub.model';
import { InitializedEvent } from 'devextreme/ui/toolbar';
import { hasPermissionGuard } from 'ag-common-svc/shared/utils/has-permission-guard';
import { AgenciesRoutes } from 'src/app/ag-crm/agencies/agencies.model';
import { FormsListRoutes } from '../../../ag-crm/reports/forms-list/forms-list.model';
import { AgentsRoutes } from 'src/app/ag-crm/agents/agents.model';
import { MenuItemsConstants } from 'ag-common-lib/public-api';
import { CarriersProductionReportingRoutes } from 'src/app/ag-carriers-production-reporting/ag-carriers-production-reporting.model';
import { ProductionImportOverridesRoutes } from 'src/app/ag-carriers-production-reporting/production-import-overrides/production-import-overrides.model';
import { REPORTS_VIEWER_PERMISSIONS } from 'src/app/reports-viewer-page/reports-viewer-permissions';

const dropDownWidth = 160;
const dropDownHeight = '10px';
const crmMenu = MenuItemsConstants.crm;
const dashboardMenu = crmMenu.dashboard;
const contactsMenuItems = crmMenu.contacts;
const registrationsMenu = crmMenu.registrations;
const notificationsHubMenu = crmMenu.notificationsHub;
const campaignsMenu = crmMenu.campaigns;
const reportsMenu = crmMenu.reports;
const administrationMenu = crmMenu.administration;

@Component({
  selector: 'app-header',
  templateUrl: 'header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
  @Output() menuToggle = new EventEmitter<boolean>();

  @Input() menuToggleEnabled = false;
  @Input() title!: string;

  toolbarItems$ = this.authService.armPermissions$.pipe(
    map(permissions => {
      return [
        {
          location: 'before',
          widget: 'dxMenu',
          locateInMenu: 'auto',
          // Mirrors DashboardGuard, so the link is not offered into an access-denied bounce.
          visible: hasPermissionGuard([Entity.dashboard], permissions),
          options: {
            orientation: 'horizontal',
            displayExpr: 'description',
            cssClass: 'ag-header__menu',
            items: [{ value: AppRoutes.Dashboard, description: dashboardMenu.dashboard }],
            onItemClick: ({ itemData }: any) => {
              if (itemData?.value) {
                this.router.navigate([itemData.value]);
              }
            },
          },
        },
        {
          location: 'before',
          widget: 'dxMenu',
          locateInMenu: 'auto',
          visible: hasPermissionGuard(
            [Entity.agentAdminister, Entity.agencyAdminister, Entity.carrierCrm, Entity.prospect, Entity.registrants],
            permissions,
          ),
          options: {
            orientation: 'horizontal',
            displayExpr: 'description',
            cssClass: 'ag-header__menu',
            items: [
              {
                description: contactsMenuItems.contacts,
                items: [
                  {
                    value: AppRoutes.Agents,
                    description: contactsMenuItems.agents,
                    visible: hasPermissionGuard([Entity.agentAdminister], permissions),
                  },
                  {
                    value: `${AppRoutes.Agents}/${AgentsRoutes.AgentsIssues}`,
                    description: contactsMenuItems.agentsIssues,
                    // TODO agents-issues task
                    visible: false,
                  },
                  {
                    value: AppRoutes.Agencies,
                    description: contactsMenuItems.agencies,
                    visible: hasPermissionGuard(
                      [Entity.agencyAdminister, Entity.agencyHierarchyAdministration],
                      permissions,
                    ),
                    items: [
                      {
                        value: AgenciesRoutes.AgenciesList,
                        routes: AppRoutes.Agencies,
                        description: contactsMenuItems.agenciesList,
                        visible: hasPermissionGuard([Entity.agencyAdminister], permissions),
                      },
                      {
                        value: AgenciesRoutes.AgencyHierarchyAdministration,
                        routes: AppRoutes.Agencies,
                        description: contactsMenuItems.agencyHierarchyAdministration,
                        visible: hasPermissionGuard([Entity.agencyHierarchyAdministration], permissions),
                      },
                    ],
                  },
                  {
                    value: AppRoutes.Carriers,
                    description: contactsMenuItems.carriers,
                    visible: hasPermissionGuard([Entity.carrierCrm], permissions),
                  },
                  {
                    value: AppRoutes.Prospects,
                    description: contactsMenuItems.prospects,
                    visible: hasPermissionGuard([Entity.prospect], permissions),
                  },
                  {
                    value: AppRoutes.Registrants,
                    description: contactsMenuItems.registrants,
                    visible: hasPermissionGuard([Entity.registrants], permissions),
                  },
                ],
              },
            ],
            onItemClick: ({ itemData }: any) => {
              if (itemData?.value) {
                itemData.routes
                  ? this.router.navigate([itemData.routes, itemData.value])
                  : this.router.navigate([itemData.value]);
              }
            },
          },
        },
        {
          location: 'before',
          widget: 'dxMenu',
          locateInMenu: 'auto',
          visible: hasPermissionGuard(
            [Entity.conference, Entity.conferenceRegistrants, Entity.conferenceListAdmin, Entity.conferenceList],
            permissions,
          ),
          options: {
            orientation: 'horizontal',
            displayExpr: 'description',
            cssClass: 'ag-header__menu',
            items: [
              {
                description: registrationsMenu.registrations,
                items: [
                  {
                    value: AppRoutes.Events,
                    description: registrationsMenu.events,
                    visible: hasPermissionGuard(
                      [Entity.conference, Entity.conferenceListAdmin, Entity.conferenceList],
                      permissions,
                    ),
                  },
                  {
                    value: AppRoutes.ConferenceRegistrations,
                    description: registrationsMenu.eventRegistrations,
                    visible: hasPermissionGuard([Entity.conferenceRegistrants], permissions),
                  },
                ],
              },
            ],
            onItemClick: ({ itemData }: any) => {
              if (itemData?.value) {
                itemData.routes
                  ? this.router.navigate([itemData.routes, itemData.value])
                  : this.router.navigate([itemData.value]);
              }
            },
          },
        },
        {
          location: 'before',
          widget: 'dxMenu',
          locateInMenu: 'auto',
          visible: hasPermissionGuard(
            [
              Entity.notificationsChannels,
              Entity.notificationsScheduler,
              Entity.notificationsTemplates,
              Entity.dynamicListGroups,
              Entity.dynamicSegmentsGroups,
              Entity.emailTemplates,
            ],
            permissions,
          ),
          options: {
            orientation: 'horizontal',
            displayExpr: 'description',
            cssClass: 'ag-header__menu',
            items: [
              {
                description: notificationsHubMenu.notificationsHub,
                items: [
                  {
                    value: NotificationsHubRoutes.NotificationsTemplates,
                    description: notificationsHubMenu.notificationsTemplates,
                    visible: hasPermissionGuard([Entity.notificationsTemplates], permissions),
                    routes: AppRoutes.NotificationsHub,
                  },
                  {
                    description: notificationsHubMenu.emailTemplates,
                    visible: hasPermissionGuard([Entity.emailTemplates], permissions),
                    items: [
                      {
                        value: NotificationsHubRoutes.EmailTemplates,
                        description: notificationsHubMenu.templates,
                        visible: true,
                        routes: AppRoutes.NotificationsHub,
                      },
                      {
                        value: NotificationsHubRoutes.EmailBlocks,
                        description: notificationsHubMenu.blocks,
                        visible: true,
                        routes: AppRoutes.NotificationsHub,
                      },
                      {
                        value: NotificationsHubRoutes.GlobalBlocks,
                        description: notificationsHubMenu.globalBlocks,
                        visible: true,
                        routes: AppRoutes.NotificationsHub,
                      },
                    ],
                  },
                  {
                    value: NotificationsHubRoutes.NotificationsChannels,
                    description: notificationsHubMenu.notificationsChannels,
                    visible: hasPermissionGuard([Entity.notificationsChannels], permissions),
                    routes: AppRoutes.NotificationsHub,
                  },
                  {
                    value: NotificationsHubRoutes.NotificationsScheduler,
                    description: notificationsHubMenu.notificationsScheduler,
                    visible: hasPermissionGuard([Entity.notificationsScheduler], permissions),
                    routes: AppRoutes.NotificationsHub,
                  },
                  {
                    description: notificationsHubMenu.listsAndSegments,
                    visible: hasPermissionGuard([Entity.dynamicSegmentsGroups, Entity.dynamicListGroups], permissions),
                    items: [
                      {
                        value: NotificationsHubRoutes.DynamicLists,
                        description: notificationsHubMenu.lists,
                        visible: hasPermissionGuard([Entity.dynamicListGroups], permissions),
                        routes: AppRoutes.NotificationsHub,
                      },
                      {
                        value: NotificationsHubRoutes.DynamicSegments,
                        description: notificationsHubMenu.segments,
                        visible: hasPermissionGuard([Entity.dynamicSegmentsGroups], permissions),
                        routes: AppRoutes.NotificationsHub,
                      },
                    ],
                  },
                ],
              },
            ],
            onItemClick: ({ itemData }: any) => {
              if (itemData?.value) {
                itemData.routes
                  ? this.router.navigate([itemData.routes, itemData.value])
                  : this.router.navigate([itemData.value]);
              }
            },
          },
        },
        {
          location: 'before',
          widget: 'dxMenu',
          locateInMenu: 'auto',
          visible: hasPermissionGuard(campaignMenuEntityList, permissions),
          options: {
            orientation: 'horizontal',
            displayExpr: 'description',
            cssClass: 'ag-header__menu',
            items: [
              {
                description: campaignsMenu.campaigns,
                items: [
                  {
                    value: CampaignsRoutes.CampaignServices,
                    description: campaignsMenu.campaignServices,
                    visible: hasPermissionGuard([Entity.campaignServices], permissions),
                    routes: AppRoutes.Campaign,
                  },
                  {
                    value: CampaignsRoutes.CampaignManagementTaskBoard,
                    description: campaignsMenu.taskBoard,
                    visible: hasPermissionGuard([Entity.campaignTaskBoard], permissions),
                    routes: AppRoutes.Campaign,
                  },
                  {
                    value: CampaignsRoutes.CampaignManagementTaskGrid,
                    description: campaignsMenu.taskGrid,
                    visible: hasPermissionGuard([Entity.campaignTaskGrid], permissions),
                    routes: AppRoutes.Campaign,
                  },
                ],
              },
            ],
            onItemClick: ({ itemData }: any) => {
              if (itemData?.value) {
                itemData.routes
                  ? this.router.navigate([itemData.routes, itemData.value])
                  : this.router.navigate([itemData.value]);
              }
            },
          },
        },
        {
          location: 'before',
          widget: 'dxMenu',
          locateInMenu: 'auto',
          visible: hasPermissionGuard(
            [
              Entity.reportChristmasCardList,
              Entity.reportsGenerationPipeline,
              Entity.agentRegistry,
              ...REPORTS_VIEWER_PERMISSIONS,
            ],
            permissions,
          ),
          options: {
            orientation: 'horizontal',
            displayExpr: 'description',
            cssClass: 'ag-header__menu',
            items: [
              {
                description: reportsMenu.reports,
                icon: 'agi-file-text',
                items: [
                  {
                    description: reportsMenu.carriersProductionReporting,
                    icon: 'agi-file-text',
                    visible:
                      hasPermissionGuard([Entity.reportsGenerationPipeline], permissions) ||
                      hasPermissionGuard(REPORTS_VIEWER_PERMISSIONS, permissions),
                    items: [
                      {
                        value: CarriersProductionReportingRoutes.CarrierProductionImportHistory,
                        description: reportsMenu.weeklyImports,
                        icon: 'export',
                        visible: hasPermissionGuard([Entity.reportsGenerationPipeline], permissions),
                        routes: AppRoutes.CarriersProductionReporting,
                      },
                      {
                        value: AppRoutes.ReportsViewer,
                        description: reportsMenu.reportsViewer,
                        icon: 'pdffile',
                        visible: hasPermissionGuard(REPORTS_VIEWER_PERMISSIONS, permissions),
                      },

                      // TODO: commented because there was created a new page "Reports Viewer"
                      // {
                      //   description: 'Reports Viewer',
                      //   icon: 'pdffile',
                      //   visible: hasPermissionGuard([Entity.reportsGenerationPipeline], permissions),
                      //
                      //   items: [
                      //     {
                      //       value: AppRoutes.TempReact,
                      //       description: '[React] Reporting',
                      //       icon: 'pdffile',
                      //       visible: hasPermissionGuard([Entity.reportsGenerationPipeline], permissions),
                      //     },
                      //     {
                      //       value: ReportIds.mgaPaid,
                      //       description: 'MGA Paid',
                      //       icon: 'pdffile',
                      //       routes: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.ProductionReportViewer}`,
                      //     },
                      //     {
                      //       value: ReportIds.personalPaid,
                      //       description: 'Personal Paid',
                      //       icon: 'pdffile',
                      //       routes: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.ProductionReportViewer}`,
                      //     },
                      //     {
                      //       value: ReportIds.rmdPaid,
                      //       description: 'RMD Paid',
                      //       icon: 'pdffile',
                      //       routes: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.ProductionReportViewer}`,
                      //     },
                      //     {
                      //       value: ReportIds.mgaSubmit,
                      //       description: 'MGA Submit',
                      //       icon: 'exportpdf',
                      //       routes: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.ProductionReportViewer}`,
                      //     },
                      //     {
                      //       value: ReportIds.personalSubmit,
                      //       description: 'Personal Submit',
                      //       icon: 'exportpdf',
                      //       routes: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.ProductionReportViewer}`,
                      //     },
                      //     {
                      //       value: ReportIds.rmdSubmit,
                      //       description: 'RMD Submit',
                      //       icon: 'exportpdf',
                      //       routes: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.ProductionReportViewer}`,
                      //     },
                      //   ],
                      // },
                      {
                        description: reportsMenu.overrides,
                        icon: 'preferences',
                        visible: hasPermissionGuard([Entity.reportsGenerationPipeline], permissions),

                        items: [
                          {
                            value: ProductionImportOverridesRoutes.OverrideDefinition,
                            description: reportsMenu.overrideDefinition,
                            icon: 'edit',
                            routes: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.ProductionImportOverrides}`,
                          },
                          {
                            value: ProductionImportOverridesRoutes.OverrideAssignment,
                            description: reportsMenu.agencyMappings,
                            icon: 'edit',
                            routes: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.ProductionImportOverrides}`,
                          },
                          {
                            value: ProductionImportOverridesRoutes.ReportTemplates,
                            description: reportsMenu.reportTemplates,
                            icon: 'detailslayout',
                            routes: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.ProductionImportOverrides}`,
                          },
                          {
                            value: ProductionImportOverridesRoutes.OverrideReportingSessions,
                            description: reportsMenu.overrideReportingSessions,
                            icon: 'agi-payroll',
                            routes: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.ProductionImportOverrides}`,
                          },
                        ],
                      },

                      {
                        description: reportsMenu.productionData,
                        icon: 'agi-file-text',
                        visible: hasPermissionGuard([Entity.reportsGenerationPipeline], permissions),
                        items: [
                          {
                            value: CarriersProductionReportingRoutes.MasterPaid,
                            description: reportsMenu.masterPaid,
                            icon: 'agi-file-text',
                            routes: AppRoutes.CarriersProductionReporting,
                          },
                          {
                            value: CarriersProductionReportingRoutes.MasterSubmit,
                            description: reportsMenu.masterSubmit,
                            icon: 'agi-file-text',
                            routes: AppRoutes.CarriersProductionReporting,
                          },
                          {
                            value: CarriersProductionReportingRoutes.Products,
                            visible: hasPermissionGuard([Entity.products, Entity.reportsGenerationPipeline], permissions),
                            description: reportsMenu.products,
                            icon: 'detailslayout',
                            routes: AppRoutes.CarriersProductionReporting,
                          },
                          {
                            value: CarriersProductionReportingRoutes.ProductionImportSupportingTables,
                            description: reportsMenu.supportingTables,
                            icon: 'rowproperties',
                            routes: AppRoutes.CarriersProductionReporting,
                          },
                          {
                            value: CarriersProductionReportingRoutes.Reconciliation,
                            description: reportsMenu.reconciliation,
                            icon: 'agi-file-text',
                            routes: AppRoutes.CarriersProductionReporting,
                          },
                        ],
                      },
                      {
                        value: AdminCrmRoutes.ReportsManager,
                        description: reportsMenu.dashboardProductionData,
                        icon: 'agi-file-text',
                        visible: hasPermissionGuard([Entity.administrationReportsManager], permissions),
                        routes: AppRoutes.Administration,
                      },
                    ],
                  },
                  {
                    value: AppRoutes.AgentRegistry,
                    description: reportsMenu.agentIds,
                    icon: 'card',
                    visible: hasPermissionGuard([Entity.agentRegistry], permissions),
                  },
                  {
                    value: AppRoutes.DownlineImports,
                    description: reportsMenu.downlineFiles,
                    icon: 'hierarchy',
                    visible: hasPermissionGuard([Entity.agentRegistry], permissions),
                  },
                  {
                    value: CarriersProductionReportingRoutes.LeadersBulletins,
                    description: reportsMenu.leadersBulletins,
                    icon: 'agi-payroll',
                    visible: hasPermissionGuard([Entity.leadersBulletins], permissions),
                    routes: AppRoutes.CarriersProductionReporting,
                  },
                  {
                    value: CarriersProductionReportingRoutes.ProductionFileCompare,
                    description: reportsMenu.fileCompareTool,
                    icon: 'notequal',
                    routes: AppRoutes.CarriersProductionReporting,
                  },
                  {
                    description: reportsMenu.formsManagement,
                    icon: 'folder',
                    visible: hasPermissionGuard([Entity.reportFormsList], permissions),
                    items: [
                      {
                        value: FormsListRoutes.FormsListManagement,
                        description: reportsMenu.formsList,
                        icon: 'detailslayout',
                        visible: hasPermissionGuard([Entity.reportFormsList], permissions),
                        routes: AppRoutes.Forms,
                      },
                      {
                        value: FormsListRoutes.FormsSubmitsList,
                        description: reportsMenu.formSubmits,
                        icon: 'check',
                        visible: hasPermissionGuard([Entity.reportFormsList], permissions),
                        routes: AppRoutes.Forms,
                      },
                    ],
                  },
                ],
              },
            ],
            onItemClick: ({ itemData }: any) => {
              if (itemData?.value) {
                itemData.routes
                  ? this.router.navigate([itemData.routes, itemData.value])
                  : this.router.navigate([itemData.value]);
              }
            },
          },
        },
        {
          location: 'before',
          widget: 'dxMenu',
          locateInMenu: 'auto',
          visible: hasPermissionGuard(administrationMenuEntityList, permissions),
          options: {
            orientation: 'horizontal',
            displayExpr: 'description',
            cssClass: 'ag-header__menu',
            items: [
              {
                description: administrationMenu.administration,
                items: [
                  {
                    description: administrationMenu.userRoles,
                    items: [
                      {
                        value: AdminCrmRoutes.UserRolesManager,
                        description: administrationMenu.assignment,
                        visible: hasPermissionGuard([Entity.administrationUserManager], permissions),
                        routes: AppRoutes.Administration,
                      },
                      {
                        value: AdminCrmRoutes.RoleManager,
                        description: administrationMenu.management,
                        visible: hasPermissionGuard([Entity.administrationRoleManager], permissions),
                        routes: AppRoutes.Administration,
                      },
                    ],
                  },
                  {
                    value: AdminCrmRoutes.TaskListManager,
                    description: administrationMenu.taskLists,
                    visible: hasPermissionGuard([Entity.administrationTaskListManager], permissions),
                    routes: AppRoutes.Administration,
                  },
                  {
                    value: AdminCrmRoutes.ImportRulesManager,
                    description: administrationMenu.importRules,
                    visible: hasPermissionGuard([Entity.administrationImportRulesManager], permissions),
                    routes: AppRoutes.Administration,
                  },
                  {
                    value: AdminCrmRoutes.LookupsManager,
                    description: administrationMenu.lookupsManager,
                    visible: hasPermissionGuard([Entity.administrationLookupsManager], permissions),
                    routes: AppRoutes.Administration,
                  },
                  {
                    value: AdminCrmRoutes.ErrorsManager,
                    description: administrationMenu.errorsManager,
                    visible: hasPermissionGuard([Entity.administrationErrorsManager], permissions),
                    routes: AppRoutes.Administration,
                  },
                  {
                    value: AdminCrmRoutes.LogsManager,
                    description: administrationMenu.logsManager,
                    visible: hasPermissionGuard([Entity.administrationLogsManager], permissions),
                    routes: AppRoutes.Administration,
                  },

                  {
                    value: AdminCrmRoutes.ReportsManager,
                    description: administrationMenu.reportsManager,
                    visible: hasPermissionGuard([Entity.administrationReportsManager], permissions),
                    routes: AppRoutes.Administration,
                  },
                  {
                    description: administrationMenu.salesGoals,
                    visible: hasPermissionGuard([Entity.agentSalesGoal, Entity.mgaSalesGoal], permissions),
                    items: [
                      {
                        value: AdminCrmRoutes.AgentSalesGoals,
                        description: administrationMenu.agentGoals,
                        visible: hasPermissionGuard([Entity.agentSalesGoal], permissions),
                        routes: AppRoutes.Administration,
                      },
                      {
                        value: AdminCrmRoutes.MgaSalesGoals,
                        description: administrationMenu.mgaGoals,
                        visible: hasPermissionGuard([Entity.mgaSalesGoal], permissions),
                        routes: AppRoutes.Administration,
                      },
                    ],
                  },

                  {
                    value: AdminCrmRoutes.GoogleAuthManagement,
                    description: administrationMenu.googleAuthManagement,
                    visible: hasPermissionGuard([Entity.administrationGoogleAuthManager], permissions),
                    routes: AppRoutes.Administration,
                  },
                  {
                    value: AdminCrmRoutes.AuthStats,
                    description: administrationMenu.authStats,
                    visible: hasPermissionGuard([Entity.administrationAuthStats], permissions),
                    routes: AppRoutes.Administration,
                  },
                  {
                    value: AdminCrmRoutes.AgentMergeTool,
                    description: administrationMenu.agentMergeTool,
                    visible: hasPermissionGuard([Entity.administrationMergeTool], permissions),
                    routes: AppRoutes.Administration,
                  },
                  {
                    value: AdminCrmRoutes.AddressesManagement,
                    description: administrationMenu.addressesManagement,
                    visible: hasPermissionGuard([Entity.administrationAddressesManagement], permissions),
                    routes: AppRoutes.Administration,
                  },
                  {
                    description: administrationMenu.usersAgreements,
                    visible: hasPermissionGuard([Entity.administrationUserAgreementManagement], permissions),
                    items: [
                      {
                        value: AdminCrmRoutes.MasterVersionsList,
                        description: administrationMenu.masterVersionsList,
                        visible: hasPermissionGuard([Entity.administrationUserAgreementManagement], permissions),
                        routes: AppRoutes.Administration,
                      },
                      {
                        value: AdminCrmRoutes.SubversionsList,
                        description: administrationMenu.subversionsList,
                        visible: hasPermissionGuard([Entity.administrationUserAgreementManagement], permissions),
                        routes: AppRoutes.Administration,
                      },
                    ],
                  },
                ],
              },
            ],
            onItemClick: ({ itemData }: any) => {
              if (itemData?.value) {
                itemData.routes
                  ? this.router.navigate([itemData.routes, itemData.value])
                  : this.router.navigate([itemData.value]);
              }
            },
          },
        },
      ];
    }),
  );

  userBoxdropDownOptions$ = this.authService.loggedInAgent$.pipe(
    map(({ p_email }) => {
      return {
        title: p_email,
        showTitle: true,
        width: 300,
        wrapperAttr: { class: 'ag-header__user-menu ' },
      };
    }),
  );

  protected readonly menuHeight = dropDownHeight;
  protected readonly AppRoutes = AppRoutes;
  protected readonly userMenuItems = crmMenu.userMenu;

  constructor(
    private authService: ARMAuthService,
    private router: Router,
  ) {}

  protected onToolbarInitialized = (e: InitializedEvent) => {
    setTimeout(() => {
      e.component.repaint();
    }, 700);
  };

  toggleMenu = () => {
    this.menuToggle.emit();
  };

  navigateToPath(location: AppRoutes) {
    switch (location) {
      case AppRoutes.Home:
      case AppRoutes.Profile:
        this.router.navigate([location]);
        break;

      case AppRoutes.Logout:
        this.authService.logOut();
        break;
    }
  }

  protected onUserMenuItemClick = ({ itemData }: any) => {
    if (itemData?.value) {
      this.navigateToPath(itemData.value);
    }
  };
}
